<template>
  <DefineTemplate>
    <BaseLineClamp
      :class="headerClass"
      :lines="1"
      tag="span"
    >
      {{ mention.source.name }}
      <template #after="{ clamped }">
        <BaseStreemTooltip v-if="clamped">
          {{ mention.source.name }}
        </BaseStreemTooltip>
      </template>
    </BaseLineClamp>
  </DefineTemplate>

  <SyndicationsPopper
    v-if="!isPublicInsights && mention?.syndications?.length && props.stream"
    v-slot="{ toggleOpen }"
    :mention="mention"
    :parent-el-ref="props.mentionCardRef"
    :stream="props.stream"
    @source-name-click="handleSourceNameClick(mention.source)"
    @syndication-clicked="syndicationClicked($event)"
  >
    <BaseButton
      data-testid="broadcast-source-button"
      menu-item
      new-design
      plain
      @click.stop.prevent="handleSourceNameClick(mention.source)"
    >
      <ReuseTemplate />
      <span
        class="tw:cursor-pointer tw:hover:underline tw:hover:text-blue-500"
        @click.stop.prevent="toggleOpen"
        >+{{ mention.syndications.length }}</span
      >
    </BaseButton>
  </SyndicationsPopper>
  <span
    v-else
    class="alternate"
  >
    <BaseButton
      data-testid="broadcast-source-button"
      menu-item
      new-design
      plain
      @click.stop.prevent="handleSourceNameClick(mention.source)"
    >
      <ReuseTemplate />
    </BaseButton>
  </span>

  <span class="tw:text-denim-900">
    {{ mention.source.location }}
  </span>

  <span class="softer tw:text-sm">
    <!-- using start_time below as a fallback since timestamp isn't always available and they seem to have the same value -->
    <TimeAgo
      :date="mention.timestamp || mention.start_time"
      :timezone="mention.source.time_zone"
    />
    <span v-if="mention.program_airing">
      · {{ mention.program_airing.name }}
    </span>
  </span>
  <ArchivedContentLabel
    v-if="hasBroadcastArchiveRequest(mention)"
    :mention="mention"
  />
</template>

<script setup lang="ts">
import { createReusableTemplate } from "@vueuse/core";
import { twMerge } from "tailwind-merge";
import { computed, inject } from "vue";

import {
  BaseButton,
  BaseLineClamp,
  BaseStreemTooltip,
} from "shared/components/base";
import TimeAgo from "shared/components/core/various/TimeAgo.vue";
import { SyndicationsPopper } from "shared/components/SyndicationsPopper/index";
import { IsPublicInsightsSymbol } from "shared/modules/Charts/types/symbols";
import { hasBroadcastArchiveRequest } from "shared/services/mention";
import { type Nullable, type Stream } from "shared/types";
import {
  type RadioClip,
  type Syndication,
  type TvCaption,
} from "shared/types/mentions";

import { ArchivedContentLabel } from "./index";

const isPublicInsights = inject<boolean>(IsPublicInsightsSymbol, false);

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();

export type BroadcastContentMention = TvCaption | RadioClip;

const baseHeaderClass: string[] = [
  "tw:text-base",
  "tw:font-bold",
  "tw:text-denim-950",
];

export interface BroadcastContentMentionCardHeaderProps {
  customHeaderClass?: string[];
  mention: BroadcastContentMention;
  mentionCardRef?: Nullable<HTMLElement>;
  stream?: Stream;
}

const props = withDefaults(
  defineProps<BroadcastContentMentionCardHeaderProps>(),
  {
    stream: undefined,
    mentionCardRef: null,
    customHeaderClass: () => [],
  }
);

const emit = defineEmits<{
  "mention-clicked": [syndication: Syndication];
  "source-name-click": [source: BroadcastContentMention["source"]];
}>();

const headerClass = computed<string>(() =>
  twMerge(baseHeaderClass, props.customHeaderClass)
);

const mention = computed<BroadcastContentMention>(() => props.mention);

function syndicationClicked(syndication: Syndication) {
  emit("mention-clicked", syndication);
}

function handleSourceNameClick(source: Syndication["source"]) {
  emit("source-name-click", source);
}
</script>
